<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '@/stores/accountStore';

const route = useRoute();
const accountStore = useAccountStore();
const { productLoading, productEnabled, productError, productChecked } =
  storeToRefs(accountStore);

const useAdminShell = computed(
  () => route.path === '/admin' || route.path.startsWith('/admin/'),
);

watch(
  useAdminShell,
  (isAdmin) => {
    if (!isAdmin) {
      void accountStore.checkProductEnabled(route.query.account_id);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div style="box-sizing: border-box; width: 100%">
    <template v-if="useAdminShell">
      <router-view />
    </template>
    <v-app v-else>
      <v-main>
        <div v-if="productLoading || !productChecked" class="door-status door-status--loading">
          <div class="door-status__progress">
            <div class="door-status__progress-bar" />
          </div>
        </div>
        <div v-else-if="!productEnabled" class="door-status" role="alert">
          <div class="door-status__content">
            <p class="door-status__title">Temporarily unavailable</p>
            <p class="door-status__message">
              {{
                productError ||
                'Vehicle ordering isn’t available right now. Please check back later or contact the dealership for assistance.'
              }}
            </p>
          </div>
        </div>
        <router-view v-else />
      </v-main>
    </v-app>
  </div>
</template>

<style scoped>
.door-status {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  min-height: 320px;
  box-sizing: border-box;
  margin: 0;
  padding: 20px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.door-status--loading {
  align-items: center;
  min-height: 80px;
}

.door-status__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  text-align: center;
}

.door-status__progress {
  width: 100%;
  height: 5px;
  overflow: hidden;
  background: #a1a1aa;
}

.door-status__progress-bar {
  width: 100%;
  height: 100%;
  background: #000;
  animation: door-status-progress 2s linear infinite;
  transform-origin: 0% 50%;
}

.door-status__title {
  margin: 0 0 8px;
  width: 100%;
  font-family: Roboto, 'Open Sans', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: #111;
  text-align: center;
}

.door-status__message {
  margin: 0;
  width: 100%;
  font-family: Roboto, 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #525252;
  text-align: center;
}

@keyframes door-status-progress {
  0% {
    transform: translateX(-100%) scaleX(0.4);
  }
  50% {
    transform: translateX(0%) scaleX(0.4);
  }
  100% {
    transform: translateX(100%) scaleX(0.4);
  }
}

@media (max-width: 767px) {
  .door-status {
    border: none;
    border-radius: 0;
  }
}
</style>
